import { computePackages } from './allocationMath';
// Item-replenishment target + the driver vocabulary, from the ONE shared
// module the server trigger mirrors (same contract as packageBasis.js).
import { computePurchaseTarget, ORDER_DRIVERS } from '../../../supabase/functions/_shared/parAndMoq.mjs';
import { vendorIsRetired } from './purchaseOptions';
import { assembleStoredComparison, selectAutomaticOption, COMPARISON_AI_CONFIDENCE_THRESHOLD } from './priceComparison.js';
import { hasProvidedPrice } from './purchaseOptions';
import { landedPlanTotals, resolvePackageQuantity, vendorOrderCharges } from './purchaseEconomics';
import { itemCountUnitPhysicalBasis, resolveNeedToPackageBasis, validatePackageConsistency } from './packageBasis.js';
// ORDER is not BILLING — a catch-weight line's cost is an estimate settled on
// a scale. See billingBasis.js.
import { buildBuyDisplay, estimatedExtendedCost, resolveBillingBasis } from './billingBasis.js';
import { ANSWERED_SOURCES } from './buyingMode';
// Confirmed same_item pairs make sibling items one buying pool — imports
// record the relationship instead of merging the items, so an item's real
// alternatives often live on its siblings. See relatedItemGroups.js.
import { expandOptionsAcrossGroup, groupMembersFor } from './relatedItemGroups';
import {
  equivalentGroupIdFor,
  getShoppingCandidatesForItem,
} from '../../../supabase/functions/_shared/shoppingCandidates.mjs';

/**
 * Purchase planning — the math behind "Optimize This Order".
 *
 * Counting decides WHAT is needed. This module decides WHERE to buy it, and
 * it exists to keep one distinction straight that the old per-line review
 * blurred:
 *
 *   Lowest ORDER cost   what it costs to cover today's need
 *                       = packages required × package price
 *   Best UNIT price     what one unit costs inside the package
 *                       = the comparable unit cost
 *
 * These disagree constantly, and the disagreement is the whole point. A
 * 96-bun case at $0.45/bun beats a 72-bun case at $0.55/bun on unit price and
 * still costs $3.50 more to satisfy a need of 72 buns, because the buyer pays
 * for the whole case and eats 24 buns of overage. Recommending the cheaper
 * unit price there spends real money to win an arithmetic contest nobody
 * entered.
 *
 * So the default recommendation is the cheapest way to cover the need, and the
 * better unit price is surfaced beside it as a fact the buyer may act on
 * deliberately — never silently on their behalf.
 *
 * Everything here is pure. The service layer persists the result; the screens
 * render it; neither does arithmetic of its own.
 */

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPositiveNumber = (value) => {
  const parsed = toFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

const roundMoney = (value) => Math.round(value * 100) / 100;
const round6 = (value) => Math.round(value * 1e6) / 1e6;

/** The four ways a plan can pick vendors. Mirrors the plan_mode CHECK. */
export const PLAN_MODES = Object.freeze({
  RECOMMENDED: 'recommended',
  LOWEST_ORDER_COST: 'lowest_order_cost',
  CONSOLIDATE: 'consolidate',
  ITEM_BY_ITEM: 'item_by_item',
});

export const PLAN_MODE_LABELS = Object.freeze({
  [PLAN_MODES.RECOMMENDED]: 'Recommended',
  [PLAN_MODES.LOWEST_ORDER_COST]: 'Lowest order cost',
  [PLAN_MODES.CONSOLIDATE]: 'Consolidate purchases',
  [PLAN_MODES.ITEM_BY_ITEM]: 'Choose item by item',
});

export const PLAN_MODE_DESCRIPTIONS = Object.freeze({
  [PLAN_MODES.RECOMMENDED]:
    'Balances purchase cost, package overage, vendor minimums, and fewer vendor orders.',
  [PLAN_MODES.LOWEST_ORDER_COST]:
    "Uses the least expensive way to fulfill each item's current need. This may create more vendor orders.",
  [PLAN_MODES.CONSOLIDATE]:
    'Prefer one vendor wherever it carries an eligible product.',
  [PLAN_MODES.ITEM_BY_ITEM]:
    'Review or override the recommendation for individual products.',
});

/* ── Need bridges ─────────────────────────────────────────────────────────── */

/**
 * One physical definition of each item's count unit, derived from its OWN
 * options (itemCountUnitPhysicalBasis) — the need side of "convert the NEED,
 * not just the package". Passed through eligibility and ranking as
 * `needBridgeByItemId`, so a package measured only in fl oz can be
 * order-ready for a case-counted need. Items whose unit has no deterministic
 * physical definition are simply absent.
 */
export function buildNeedBridgeIndex({ itemsById = {}, optionsByItemId = {} } = {}) {
  const index = {};
  for (const [itemId, options] of Object.entries(optionsByItemId || {})) {
    const item = itemsById?.[itemId] ?? null;
    if (!item) continue;
    const bridge = itemCountUnitPhysicalBasis({ item, options });
    if (bridge) index[String(itemId)] = bridge;
  }
  return index;
}

const bridgeForOption = (option, ctx) => {
  const key = option?.inventory_item_id ?? ctx?.inventoryItemId ?? null;
  if (key == null) return null;
  return ctx?.needBridgeByItemId?.[String(key)] ?? null;
};

/* ── Eligibility ──────────────────────────────────────────────────────────── */

/**
 * Whether an option may be chosen to fill a need.
 *
 * An option that fails any of these is not a "vendor choice" — it must not
 * produce a comparison card, must not count toward "items you can optimize",
 * and must not be picked automatically. An item whose only option is
 * ineligible is not a required purchase either; it is unbuyable, and the line
 * carries that as a warning rather than silently costing $0.
 */
export function isEligibleOption(option, {
  storeId = null, inventoryItemId = null, item = null, groupMembers = null,
  candidateItemIds = null, itemsById = {}, needBridgeByItemId = null,
} = {}) {
  if (!option || option.id == null) return false;
  if (option.status !== 'active') return false;
  if (option.is_approved !== true) return false;
  if (option.is_hidden_from_allocation === true) return false;
  // A retired vendor's product may still describe stock on the shelf, but it
  // is never a candidate in the Savings Review — its price and MOQ stay out
  // of every new-order calculation.
  if (vendorIsRetired(option)) return false;
  // Same price contract as listAllocatableOptions (hasProvidedPrice): an
  // option priced only via normalized_unit_cost still counts. The two
  // predicates forking here is how a store's item allocated at plan creation
  // and then vanished from the worksheet as "no eligible option".
  if (!hasProvidedPrice(option)) return false;
  // Item scope now spans the line's confirmed same_item group: a sibling
  // item's option is a real way to buy this line (`groupMembers` is that
  // group as a Set of item id strings — see relatedItemGroups). Without one,
  // exactly the old item-scoped rule; either way an unrelated item's option
  // is rejected, and the store check below still rejects cross-store options.
  if (inventoryItemId != null && option.inventory_item_id != null
      && String(option.inventory_item_id) !== String(inventoryItemId)
      && !groupMembers?.has?.(String(option.inventory_item_id))
      && !candidateItemIds?.has?.(String(option.inventory_item_id))) return false;
  if (storeId != null && option.store_id != null
      && String(option.store_id) !== String(storeId)) return false;
  // The order-package basis must be resolved and self-consistent. An option
  // whose stored facts describe different packages (the prorated-bag /
  // whole-case contradiction), or that cannot say what one package contains,
  // is a repair state — it must not produce a comparison card, win a badge,
  // enter a subtotal, or be picked automatically. reviewableOptionsFor()
  // carries these to the UI so the line says "package needs review" instead
  // of silently costing $0 or rerouting to another vendor.
  const optionItem = itemsById?.[String(option.inventory_item_id)] || item;
  const needBridge = bridgeForOption(option, { inventoryItemId, needBridgeByItemId });
  if (!validatePackageConsistency(option, { item: optionItem, needBridge }).orderReady) return false;
  return true;
}

/** The eligible subset, order preserved. */
export function eligibleOptionsFor(options, ctx = {}) {
  return (Array.isArray(options) ? options : []).filter((o) => isEligibleOption(o, ctx));
}

/**
 * Options that WOULD be candidates but fail package-basis readiness: active,
 * approved, visible, priced products whose stored facts conflict or whose
 * package contents are unresolved. These are what the "Package needs review"
 * card lists — an ordinary predicate failure (inactive, hidden, retired,
 * unpriced) is not a repair state and does not appear here.
 */
export function reviewableOptionsFor(options, ctx = {}) {
  return (Array.isArray(options) ? options : [])
    .filter((option) => {
      if (!option || option.id == null) return false;
      if (option.status !== 'active') return false;
      if (option.is_approved !== true) return false;
      if (option.is_hidden_from_allocation === true) return false;
      if (vendorIsRetired(option)) return false;
      if (!hasProvidedPrice(option)) return false;
      if (ctx.inventoryItemId != null && option.inventory_item_id != null
          && String(option.inventory_item_id) !== String(ctx.inventoryItemId)
          && !ctx.groupMembers?.has?.(String(option.inventory_item_id))
          && !ctx.candidateItemIds?.has?.(String(option.inventory_item_id))) return false;
      if (ctx.storeId != null && option.store_id != null
          && String(option.store_id) !== String(ctx.storeId)) return false;
      const optionItem = ctx.itemsById?.[String(option.inventory_item_id)] || ctx.item;
      return !validatePackageConsistency(option, {
        item: optionItem, needBridge: bridgeForOption(option, ctx),
      }).orderReady;
    })
    .map((option) => {
      const optionItem = ctx.itemsById?.[String(option.inventory_item_id)] || ctx.item;
      const check = validatePackageConsistency(option, {
        item: optionItem, needBridge: bridgeForOption(option, ctx),
      });
      return {
        optionId: String(option.id),
        option,
        status: check.status,
        warnings: check.warnings,
        details: check.details,
        basis: check.basis,
      };
    });
}

/**
 * Splits allocation lines by whether their inventory item is still live.
 *
 * An item archived after its count was planned must drop out of the Savings
 * Review — it is not shown, not costed, and (matching the fan-out RPCs'
 * guard) not ordered. Same liveness rule as countItemLiveness's
 * isLiveInventoryItem, and the same preserve-unknown stance: a line whose
 * item row could not be loaded is kept, because a degraded fetch must not
 * silently hide a purchase.
 *
 * @returns {{ live: Array, archived: Array }}
 */
export function splitLinesByItemLiveness(lines = [], itemsById = {}) {
  const live = [];
  const archived = [];
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const item = itemsById[line?.inventory_item_id];
    if (item && (item.is_active === false || item.deleted_at)) archived.push(line);
    else live.push(line);
  });
  return { live, archived };
}

/**
 * True when at least one needed item has a real vendor decision to make.
 *
 * This is the routing question for the whole flow: a plan where nothing has a
 * second eligible source has nothing to optimize, and showing an empty
 * optimization screen to say so wastes the buyer's time. Such a plan goes
 * straight to PO review.
 */
export function hasVendorChoices(lines, optionsByItemId = {}, ctx = {}) {
  // With `ctx.groups` (buildSameItemGroups over the store's confirmed
  // same_item pairs) each line shops its whole group: the pool is expanded
  // across siblings and the eligibility check accepts their options. Without
  // groups this is exactly the old item-scoped question.
  const {
    groups = null,
    shoppingCandidateGraph = null,
    itemsById = {},
    canonicalItemIdByGroupId = {},
    ...optionCtx
  } = ctx;
  const pools = groups
    ? expandOptionsAcrossGroup(optionsByItemId, groups)
    : (optionsByItemId || {});
  const needBridgeByItemId = optionCtx.needBridgeByItemId
    ?? buildNeedBridgeIndex({ itemsById, optionsByItemId });
  return (Array.isArray(lines) ? lines : []).some((line) => {
    const itemId = line?.inventory_item_id;
    const shopping = shoppingCandidateGraph
      ? getShoppingCandidatesForItem(itemId, {
        graph: shoppingCandidateGraph,
        itemsById,
        optionsByItemId,
        canonicalItemIdByGroupId,
      })
      : null;
    const options = shopping?.purchaseOptions || pools[itemId] || [];
    const candidateItemIds = shopping
      ? new Set([
        ...(shopping.primaryGroup?.itemIds || []),
        ...shopping.substituteGroups.flatMap((group) => group.itemIds),
      ])
      : null;
    const eligible = eligibleOptionsFor(options, {
      ...optionCtx,
      inventoryItemId: itemId,
      groupMembers: groupMembersFor(groups, itemId),
      candidateItemIds,
      itemsById,
      needBridgeByItemId,
    });
    const reviewable = reviewableOptionsFor(options, {
      ...optionCtx,
      inventoryItemId: itemId,
      groupMembers: groupMembersFor(groups, itemId),
      candidateItemIds,
      itemsById,
      needBridgeByItemId,
    });
    const candidateIds = new Set([
      ...eligible.map((option) => String(option.id)),
      ...reviewable.map((entry) => String(entry.optionId)),
    ]);
    return candidateIds.size >= 2;
  });
}

/* ── Cost to fulfil one need ──────────────────────────────────────────────── */

/**
 * What one option costs to cover `needQty` of an item.
 *
 * `orderCost` is the number that decides a purchase: whole packages at the
 * package price. `comparableUnitCost` is supplied by the caller from the
 * stored comparison and is NOT used for ranking here — it is carried so the
 * screen can show both and name which is which.
 *
 * When `item` carries replenishment_minimum_qty / replenishment_increment_qty
 * the item-target stage applies exactly as buildAllocationLine (allocationMath)
 * does: the package arithmetic pursues the TARGET, `overage` stays measured
 * against the RAW need, and `driver` names the one decisive constraint.
 *
 * @returns {{ optionId, packages, orderCost, quantityReceived, overage,
 *             packagePrice, comparableUnitCost, rawNeed, purchaseTarget,
 *             targetDriver, driver }|null}
 */
export function costToFulfill(
  needQty,
  option,
  comparableUnitCost = null,
  { needUnit = null, item = null, optionItem = null, needBridge = null, optionNeedBridge = null } = {},
) {
  if (!option) return null;
  const packagePrice = toPositiveNumber(option.package_price);
  const readinessItem = needUnit
    ? { ...(item || {}), unit: item?.unit ?? item?.count_unit ?? needUnit }
    : item;
  const packageItem = optionItem
    ? {
      ...optionItem,
      unit: optionItem.unit ?? optionItem.count_unit ?? needUnit ?? readinessItem?.unit ?? null,
    }
    : readinessItem;
  // Package quantity through the one shared resolver: a human-confirmed
  // interpretation wins, then structured inner-pack fields, then the stored
  // column — never a re-parse of pack text at display time.
  // Readiness is judged in the OPTION's item's unit (its own bridge); the
  // need conversion below is judged in the LINE's unit (the line item's
  // bridge). For a same-item option they are the same object.
  const consistency = validatePackageConsistency(option, {
    item: packageItem,
    needBridge: optionNeedBridge ?? needBridge,
  });
  if (needUnit && !consistency.orderReady) return null;
  const pkg = resolvePackageQuantity(option);
  const needBasis = needUnit
    ? resolveNeedToPackageBasis({
      needQty,
      needUnit,
      option,
      packageBasis: consistency.basis,
      item: packageItem,
      needBridge,
    })
    : null;
  if (needBasis && !needBasis.compatible) return null;
  const quantityPerPackage = needBasis?.quantityPerPackageInNeedUnit ?? pkg.quantity;
  const rawNeed = toFiniteNumber(needQty) || 0;
  const targeting = computePurchaseTarget({
    rawNeed,
    itemMinimum: item?.replenishment_minimum_qty,
    itemIncrement: item?.replenishment_increment_qty,
  });
  const purchaseTarget = targeting.target ?? rawNeed;
  const { packages, quantityReceived, overage, driver } = computePackages({
    needQty: purchaseTarget,
    qtyPerPackage: quantityPerPackage,
    moqPackages: option.moq_packages,
    allowsFractional: option.allows_fractional_packages === true,
    orderIncrement: option.order_increment,
  });
  // ── ORDER is not BILLING ────────────────────────────────────────────────
  //
  // A catch-weight vendor sells a case and invoices per pound. When the row
  // proves that shape the order cost becomes packages × est. weight × rate and
  // is marked estimated; every other row keeps packages × packagePrice exactly.
  // Packages, received quantity and overage above are untouched either way.
  const billingEstimate = estimatedExtendedCost(option, packages, { item: readinessItem });
  const exactOrderCost = packagePrice === null ? null : roundMoney(packages * packagePrice);

  return {
    optionId: String(option.id),
    option,
    packages,
    packagePrice,
    // With an unusable package quantity the MOQ still gets ordered and still
    // gets billed — the cost is real even when the contents are unresolved.
    // Leaving it null made stored plan totals disagree with the worksheet.
    orderCost: billingEstimate ? billingEstimate.amount : exactOrderCost,
    costIsEstimated: billingEstimate !== null,
    billingBasisText: billingEstimate ? billingEstimate.basisText : null,
    quantityReceived,
    // Extra vs the RAW need, matching what the confirm trigger freezes.
    overage: quantityReceived != null ? round6(quantityReceived - rawNeed) : overage,
    packageUnresolved: quantityPerPackage === null || pkg.requiresReview,
    quantityPerPackage,
    needConversionSource: needBasis?.source ?? pkg.source,
    comparableUnitCost: toPositiveNumber(comparableUnitCost),
    rawNeed,
    purchaseTarget: targeting.target,
    targetDriver: targeting.driver,
    // The one decisive constraint across both layers: the vendor layer names
    // itself when it raised the buy, else the item layer's driver stands.
    driver: driver === ORDER_DRIVERS.NEED ? targeting.driver : driver,
  };
}

/**
 * Every eligible way to cover a need, each labelled with how it wins.
 *
 * `isLowestOrderCost` and `isBestUnitPrice` are computed independently and
 * routinely land on different options — that divergence is what the buyer is
 * being shown. `extraQuantity` is the overage the better unit price costs, so
 * "24 extra buns" appears next to the claim rather than being discovered
 * after delivery.
 */
export function rankFulfillment({
  needQty,
  needUnit = null,
  item = null,
  options = [],
  comparison = null,
  optionRoleById = {},
  itemsById = {},
  needBridgeByItemId = null,
} = {}) {
  const entryById = new Map(
    (comparison?.options || []).map((entry) => [String(entry.optionId), entry]),
  );

  // Bridges: the LINE item's for converting the need, each OPTION item's for
  // its own readiness. When the caller supplied no index, derive from the
  // pool at hand (each item's own options within it).
  const bridges = needBridgeByItemId
    ?? buildNeedBridgeIndex({
      itemsById: {
        ...(item?.id != null ? { [String(item.id)]: item } : {}),
        ...(itemsById || {}),
      },
      optionsByItemId: options.reduce((acc, option) => {
        const key = option?.inventory_item_id ?? item?.id ?? null;
        if (key == null) return acc;
        (acc[String(key)] ||= []).push(option);
        return acc;
      }, {}),
    });
  const lineBridge = item?.id != null ? bridges?.[String(item.id)] ?? null : null;

  const rows = options
    .map((option) => {
      const entry = entryById.get(String(option.id)) || null;
      // Comparable cost is DERIVED, unrounded: package price ÷ quantity in
      // the comparison unit. A stored cost on the entry is accepted only when
      // the quantity is missing — the quotient is the truth.
      const price = toPositiveNumber(option.package_price);
      const compQty = toPositiveNumber(entry?.quantityPerPurchasePackage);
      const comparable = price !== null && compQty !== null
        ? price / compQty
        : (entry?.comparableUnitCost ?? null);
      const role = optionRoleById?.[String(option.id)] || 'primary';
      const row = costToFulfill(needQty, option, comparable, {
        needUnit,
        item,
        optionItem: itemsById?.[String(option.inventory_item_id)] || null,
        needBridge: lineBridge,
        optionNeedBridge: option?.inventory_item_id != null
          ? bridges?.[String(option.inventory_item_id)] ?? null
          : lineBridge,
      }) || {
        optionId: String(option.id),
        option,
        packages: null,
        orderCost: null,
        quantityReceived: null,
        overage: null,
        packagePrice: toPositiveNumber(option.package_price),
        comparableUnitCost: comparable,
        rawNeed: toFiniteNumber(needQty) || 0,
        purchaseTarget: null,
        targetDriver: null,
        driver: null,
        fulfillmentResolvable: false,
      };
      const estimated = entry?.estimated === true || entry?.source === 'ai_estimate';
      const confidence = Number(entry?.confidence);
      row.candidateRole = role;
      row.isSubstitute = role === 'substitute';
      // Preserve the comparison facts on the ranked row. Moving a line picks a
      // different row from this same set; if these facts live only in the
      // temporary `entry` above, the plan can still choose correctly but its
      // scenario diff can no longer explain what changed after the choice.
      // They are display/diff facts only — fulfillment continues to use the
      // authoritative package-basis result returned by costToFulfill.
      row.comparisonQuantityPerPackage = compQty;
      row.comparisonSource = entry?.source ?? null;
      row.comparisonConfidence = entry?.confidence ?? null;
      row.comparisonEstimated = estimated;
      row.comparisonBasisVerified = entry?.basisVerified !== false;
      if (row.fulfillmentResolvable !== false) row.fulfillmentResolvable = true;
      // A low-confidence AI estimate may display, but it cannot hold the
      // Best Unit Price title — same bar the automatic selection applies.
      // Neither can a comparison that does not provably describe the CURRENT
      // package (basisVerified false: no fingerprint and no stored-data
      // proof) — it shows, but it crowns nothing until a recompute stamps it.
      row.comparisonTrustworthy = row.comparableUnitCost !== null
        && entry?.basisVerified !== false
        && (!estimated || (Number.isFinite(confidence) && confidence >= COMPARISON_AI_CONFIDENCE_THRESHOLD));
      return row;
    })
    .filter(Boolean);

  // Automatic titles and choices are primary-only. Substitutes are compared
  // beside them, but a cheaper different product is an invitation for the
  // buyer to decide, never a silent replacement of the Count Item.
  const primaryRows = rows.filter((row) => !row.isSubstitute);
  const costs = primaryRows.map((r) => r.orderCost).filter((c) => c !== null);
  const unitCosts = primaryRows
    .filter((r) => r.comparisonTrustworthy)
    .map((r) => r.comparableUnitCost)
    .filter((c) => c !== null);
  const lowestCost = costs.length ? Math.min(...costs) : null;
  const bestUnit = unitCosts.length ? Math.min(...unitCosts) : null;
  const allUnitCosts = rows
    .filter((r) => r.comparisonTrustworthy)
    .map((r) => r.comparableUnitCost)
    .filter((c) => c !== null);
  const lowestShoppingUnit = allUnitCosts.length ? Math.min(...allUnitCosts) : null;
  const substituteCosts = rows
    .filter((r) => r.isSubstitute && r.comparisonTrustworthy)
    .map((r) => r.comparableUnitCost)
    .filter((c) => c !== null);
  const bestSubstituteUnit = substituteCosts.length ? Math.min(...substituteCosts) : null;

  return rows.map((row) => ({
    ...row,
    isLowestOrderCost: !row.isSubstitute && lowestCost !== null && row.orderCost === lowestCost,
    isBestUnitPrice: !row.isSubstitute && bestUnit !== null
      && row.comparisonTrustworthy && row.comparableUnitCost === bestUnit,
    isLowestShoppingUnitPrice: lowestShoppingUnit !== null && row.comparisonTrustworthy
      && row.comparableUnitCost === lowestShoppingUnit,
    isLowestSubstitutePrice: row.isSubstitute && bestSubstituteUnit !== null
      && row.comparisonTrustworthy && row.comparableUnitCost === bestSubstituteUnit,
    substituteSavingsPerUnit: row.isSubstitute && bestUnit !== null
      && row.comparisonTrustworthy && row.comparableUnitCost < bestUnit
      ? round6(bestUnit - row.comparableUnitCost)
      : null,
    // What choosing the best unit price costs over the cheapest order, in
    // money and in product nobody asked for.
    excessCost: lowestCost !== null && row.orderCost !== null
      ? roundMoney(row.orderCost - lowestCost)
      : null,
    extraQuantity: row.overage !== null && row.overage > 0 ? row.overage : null,
  }));
}

/* ── Choosing one option per line ─────────────────────────────────────────── */

/**
 * Which option a given mode picks for one line, and why.
 *
 * An explicit choice always wins — a person deciding is not an input to an
 * optimizer, it is the answer. Beyond that:
 *
 *   lowest_order_cost  cheapest way to cover the need
 *   consolidate        the preferred vendor when it has an eligible option,
 *                      otherwise fall through to recommended
 *   recommended        cheapest order cost, but deferring to the stored
 *                      comparison's automatic pick when prices cannot be
 *                      compared, so an unresolved item still buys sensibly
 *   item_by_item       same as recommended until the user overrides
 *
 * @returns {{ optionId: string|null, reason: string }}
 */
export function chooseOptionForLine({
  needQty,
  needUnit = null,
  item = null,
  options = [],
  comparison = null,
  mode = PLAN_MODES.RECOMMENDED,
  preferredVendorId = null,
  explicitOptionId = null,
  optionRoleById = {},
  itemsById = {},
  needBridgeByItemId = null,
} = {}) {
  if (!options.length) return { optionId: null, reason: 'no_eligible_option' };

  if (explicitOptionId != null && explicitOptionId !== '') {
    const pinned = options.find((o) => String(o.id) === String(explicitOptionId));
    if (pinned) return { optionId: String(pinned.id), reason: 'explicit' };
  }

  const primaryOptions = options.filter(
    (option) => (optionRoleById?.[String(option.id)] || 'primary') !== 'substitute',
  );
  if (primaryOptions.length === 0) {
    return { optionId: null, reason: 'substitute_requires_choice' };
  }
  if (primaryOptions.length === 1) return { optionId: String(primaryOptions[0].id), reason: 'only' };

  const ranked = rankFulfillment({
    needQty, needUnit, item, options: primaryOptions, comparison, optionRoleById, itemsById, needBridgeByItemId,
  });
  const cheapest = ranked.find((r) => r.isLowestOrderCost) || null;

  if (mode === PLAN_MODES.CONSOLIDATE && preferredVendorId) {
    const fromPreferred = ranked.filter(
      (r) => r.option.vendor_id != null && String(r.option.vendor_id) === String(preferredVendorId),
    );
    if (fromPreferred.length) {
      // Cheapest way to buy it FROM that vendor, not the cheapest overall.
      const best = fromPreferred.reduce(
        (a, b) => ((b.orderCost ?? Infinity) < (a.orderCost ?? Infinity) ? b : a),
      );
      return { optionId: best.optionId, reason: 'consolidated' };
    }
    // The preferred vendor does not carry this. Consolidation is a preference,
    // not a constraint — the item still has to be bought somewhere.
  }

  if (mode === PLAN_MODES.LOWEST_ORDER_COST) {
    if (cheapest) return { optionId: cheapest.optionId, reason: 'lowest_order_cost' };
  }

  // Recommended / item_by_item default. When the stored comparison cannot rank
  // these options, fall back to its own automatic choice (preferred vendor,
  // sole active option) rather than inventing a winner from prices that were
  // never comparable.
  if (comparison?.comparisonUnit) {
    if (cheapest) return { optionId: cheapest.optionId, reason: 'lowest_order_cost' };
  }

  const auto = selectAutomaticOption({ options: primaryOptions, comparison });
  if (auto.optionId != null) return { optionId: String(auto.optionId), reason: auto.reason };

  const preferred = primaryOptions.find((o) => o.is_preferred === true);
  if (preferred) return { optionId: String(preferred.id), reason: 'preferred_fallback' };
  if (cheapest) return { optionId: cheapest.optionId, reason: 'lowest_order_cost' };
  return { optionId: null, reason: 'unresolved' };
}

/* ── The whole plan ───────────────────────────────────────────────────────── */

/**
 * Applies a mode across every line and groups the result by vendor.
 *
 * Single-source lines are planned exactly like the rest — they carry cost,
 * they land in a vendor's order, and they decide whether that vendor's
 * minimum is met. They are only excluded from the *comparison controls*,
 * never from the totals. A plan that reported only the optimizable subtotal
 * would understate every order it produced.
 *
 * @param {object} args
 * @param {Array}  args.lines             allocation lines (need + item id)
 * @param {object} args.optionsByItemId   inventory_item_id → purchase_options[]
 * @param {object} args.comparisonByItemId inventory_item_id → stored comparison
 * @param {object} args.explicitByLineId  line id → pinned option id
 * @param {object} args.vendorsById       vendor id → vendor row
 */
export function buildPurchasePlan({
  lines = [],
  optionsByItemId = {},
  comparisonByItemId = {},
  explicitByLineId = {},
  vendorsById = {},
  itemsById = {},
  mode = PLAN_MODES.RECOMMENDED,
  preferredVendorId = null,
  storeId = null,
  groups = null,
  shoppingCandidateGraph = null,
  canonicalItemIdByGroupId = {},
  // Scenario-only constraints. They remove vendor products from this plan
  // evaluation; they never mutate purchase_options or the vendor record.
  // A line-level allowance is the explicit escape hatch for an impossible
  // scenario ("keep Restaurant Depot for 7UP only").
  excludedVendorIds = [],
  allowedExcludedVendorIdsByLineId = {},
} = {}) {
  // With `groups` each line shops its whole EQUIVALENT group — the same
  // expansion hasVendorChoices routes on, so the worksheet can never route a
  // plan into review over a sibling option it then refuses to show.
  const pools = groups ? expandOptionsAcrossGroup(optionsByItemId, groups) : (optionsByItemId || {});
  // Each item's count-unit physical definition, derived once from its OWN
  // (unpooled) options — the pooled list would let a sibling item's package
  // define this item's unit.
  const needBridgeByItemId = buildNeedBridgeIndex({ itemsById, optionsByItemId });
  const excludedVendors = new Set(
    [...(excludedVendorIds || [])].map(String),
  );
  const planLines = lines.map((line) => {
    const itemId = line.inventory_item_id;
    const shopping = shoppingCandidateGraph
      ? getShoppingCandidatesForItem(itemId, {
        graph: shoppingCandidateGraph,
        itemsById,
        optionsByItemId,
        canonicalItemIdByGroupId,
      })
      : null;
    const unrestrictedCandidateOptions = shopping?.purchaseOptions || pools[itemId] || [];
    const allowedForLine = new Set(
      [...(allowedExcludedVendorIdsByLineId?.[String(line.id)] || [])].map(String),
    );
    const candidateOptions = excludedVendors.size === 0
      ? unrestrictedCandidateOptions
      : unrestrictedCandidateOptions.filter((option) => {
        if (option?.vendor_id == null) return true;
        const vendorId = String(option.vendor_id);
        return !excludedVendors.has(vendorId) || allowedForLine.has(vendorId);
      });
    const optionRoleById = shopping?.optionRoleById || Object.fromEntries(
      candidateOptions.map((option) => [String(option.id), 'primary']),
    );
    const candidateItemIds = shopping
      ? new Set([
        ...(shopping.primaryGroup?.itemIds || []),
        ...shopping.substituteGroups.flatMap((group) => group.itemIds),
      ])
      : null;
    const ctx = {
      storeId,
      inventoryItemId: itemId,
      item: itemsById[itemId] ?? null,
      groupMembers: groupMembersFor(groups, itemId),
      candidateItemIds,
      itemsById,
      needBridgeByItemId,
    };
    const eligible = eligibleOptionsFor(candidateOptions, ctx);
    const reviewable = reviewableOptionsFor(candidateOptions, ctx);
    const primaryEligible = eligible.filter(
      (option) => optionRoleById[String(option.id)] !== 'substitute',
    );
    const substituteEligible = eligible.filter(
      (option) => optionRoleById[String(option.id)] === 'substitute',
    );
    const primaryReviewable = reviewable.filter(
      ({ optionId }) => optionRoleById[String(optionId)] !== 'substitute',
    );
    const substituteReviewable = reviewable.filter(
      ({ optionId }) => optionRoleById[String(optionId)] === 'substitute',
    );
    const comparison = comparisonByItemId[itemId] || null;
    const needQty = toFiniteNumber(line.quantity_needed) || 0;

    const explicitOptionId = explicitByLineId[line.id] ?? line.selected_purchase_option_id ?? null;
    // A chosen option that has become invalid must surface as a repair state,
    // never be silently replaced by another vendor. The buyer picked a
    // package; if that package's definition is now in dispute the decision is
    // theirs to remake, not the planner's to overrule.
    const pinnedNeedsRepair = explicitOptionId != null
      && reviewable.some((r) => String(r.optionId) === String(explicitOptionId));
    const needsPackageRepair = pinnedNeedsRepair
      || (primaryEligible.length === 0 && primaryReviewable.length > 0);

    const choice = needsPackageRepair
      ? { optionId: null, reason: 'package_needs_review' }
      : chooseOptionForLine({
        needQty,
        needUnit: line.count_unit ?? ctx.item?.unit ?? null,
        item: ctx.item,
        options: eligible,
        comparison,
        mode,
        preferredVendorId,
        explicitOptionId,
        optionRoleById,
        itemsById,
        needBridgeByItemId,
      });

    const ranked = rankFulfillment({
      needQty,
      needUnit: line.count_unit ?? ctx.item?.unit ?? null,
      item: ctx.item,
      options: eligible,
      comparison,
      optionRoleById,
      itemsById,
      needBridgeByItemId,
    });
    const chosen = ranked.find((r) => r.optionId === choice.optionId) || null;
    const cheapest = ranked.find((r) => r.isLowestOrderCost) || null;
    // The item-target stage is vendor-independent — computed here once so the
    // line can state its target even when nothing is selected yet.
    const targeting = computePurchaseTarget({
      rawNeed: needQty,
      itemMinimum: ctx.item?.replenishment_minimum_qty,
      itemIncrement: ctx.item?.replenishment_increment_qty,
    });

    return {
      lineId: line.id,
      inventoryItemId: itemId,
      description: line.description ?? ctx.item?.description ?? null,
      countUnit: line.count_unit ?? null,
      quantityNeeded: needQty,
      purchaseTarget: targeting.target,
      targetDriver: targeting.driver,
      orderDriver: chosen?.driver ?? null,
      // "Required" means one eligible source, so there is nothing to decide.
      // It does not mean cheap, unimportant, or excluded from the total.
      isRequired: primaryEligible.length === 1 && substituteEligible.length === 0 && !needsPackageRepair,
      hasChoice: eligible.length >= 2 && !needsPackageRepair,
      hasSubstitutes: substituteEligible.length > 0 || substituteReviewable.length > 0,
      isUnbuyable: primaryEligible.length === 0 && primaryReviewable.length === 0,
      // Package repair: every would-be candidate's stored facts are in
      // dispute (or the pinned one's are). No cost is fabricated for it — not
      // $28.13 from a prorated bag, and not $0 either.
      needsPackageRepair,
      packageReviewOptions: reviewable,
      substituteReviewOptions: substituteReviewable,
      optionRoleById,
      substituteGroups: shopping?.substituteGroups || [],
      options: ranked,
      selectedOptionId: choice.optionId,
      selectionReason: choice.reason,
      selected: chosen,
      orderCost: chosen?.orderCost ?? null,
      // Whether this line's total is settled on a scale, and the rate it is
      // settled at ("$16.40/LB × ~60 LB"). A surface printing orderCost as
      // money must read these first.
      costIsEstimated: chosen?.costIsEstimated === true,
      billingBasisText: chosen?.billingBasisText ?? null,
      // What the card should SAY is being ordered. For a catch-weight package
      // that is "1 case · 4 pieces · avg ~60 LB total · $16.40/LB", not the
      // "1 LB · $16.40" a fixed-package reading produced. Null for ordinary
      // fixed packages, which keep their existing wording.
      buyDisplay: chosen?.option
        ? buildBuyDisplay({
          line: {
            packages_required: chosen.packages,
            quantity_needed: line.quantity_needed,
            count_unit: line.count_unit,
            // Fixed-package cards must speak in the inventory/count unit the
            // package math actually resolved ("6 packs", not the tautology
            // "1 case"). Catch-weight billing still uses the same billing
            // basis below; these are the deterministic fulfillment results.
            quantity_received: chosen.quantityReceived,
            overage: chosen.overage,
          },
          option: chosen.option,
          basis: resolveBillingBasis(chosen.option, { item: itemsById?.[line.inventory_item_id] ?? null }),
        })
        : null,
      lowestPossibleCost: cheapest?.orderCost ?? null,
      comparisonUnit: comparison?.comparisonUnit ?? null,
      needsComparisonReview: eligible.length >= 2 && !comparison?.comparisonUnit,
      vendorId: chosen?.option?.vendor_id ?? null,
    };
  });

  return {
    mode,
    preferredVendorId,
    lines: planLines,
    vendorOrders: groupPlanByVendor(planLines, vendorsById),
    summary: summarizePlan(planLines),
  };
}

/**
 * Vendor-level view of a plan: what each vendor is being asked to ship, and
 * whether that order clears their minimum.
 *
 * `minimumMet` is deliberately tri-state. A vendor with no numeric minimum
 * recorded returns null, not true — "we do not know" and "the minimum is met"
 * are different claims, and printing "Minimum met" from an unknown is how a
 * buyer discovers at delivery that it was not.
 */
const vendorGroupKey = (line) => {
  if (line?.vendorId != null) return `vendor:${String(line.vendorId)}`;
  const raw = String(line?.selected?.option?.vendor_name_text || '').trim();
  const normalized = raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return normalized ? `unlinked:${normalized}` : '__unassigned__';
};

export function groupPlanByVendor(planLines, vendorsById = {}) {
  const groups = new Map();

  planLines.forEach((line) => {
    if (!line.selectedOptionId) return;
    const vendorId = line.vendorId ?? null;
    // Old/imported purchase options can retain the vendor name while their
    // vendor_id is still unlinked. Those are different suppliers, not one
    // giant "unassigned" supplier. Keep them visibly separate while the data
    // repair links them to canonical vendor rows; otherwise the first line's
    // name labels every other supplier's products and the review appears to
    // buy the whole order from that one vendor.
    const rawVendorName = String(line.selected?.option?.vendor_name_text || '').trim();
    const key = vendorGroupKey(line);
    if (!groups.has(key)) {
      const vendor = vendorId === null ? null : vendorsById[vendorId] || null;
      groups.set(key, {
        groupKey: key,
        vendorId,
        vendorLinked: vendorId !== null,
        vendor,
        vendorName: vendor?.display_name
          || rawVendorName
          || (vendorId === null ? 'Vendor not linked' : 'Unknown vendor'),
        fulfillmentType: vendor?.fulfillment_type || 'delivery',
        lines: [],
        subtotal: 0,
        // The catch-weight portion of this vendor's subtotal, kept apart so
        // "$1,284.00" can be shown as "about $1,284.00" when part of it is
        // decided by a scale rather than a price list.
        estimatedSubtotal: 0,
        estimatedLineCount: 0,
      });
    }
    const group = groups.get(key);
    group.lines.push(line);
    group.subtotal = roundMoney(group.subtotal + (line.orderCost || 0));
    if (line.costIsEstimated) {
      group.estimatedSubtotal = roundMoney(group.estimatedSubtotal + (line.orderCost || 0));
      group.estimatedLineCount += 1;
    }
  });

  return [...groups.values()]
    .map((group) => {
      // One charge model for every consumer. The worksheet used to compute
      // the below-minimum penalty inline, so any other surface summing the
      // same plan disagreed with it by exactly that penalty.
      const charges = vendorOrderCharges({ vendor: group.vendor, subtotal: group.subtotal });
      return {
        ...group,
        itemCount: group.lines.length,
        minimumOrderAmount: charges.minimumOrderAmount,
        // null = no minimum on record. Not the same as "met".
        minimumMet: charges.minimumMet,
        shortfall: charges.shortfall,
        deliveryFee: toFiniteNumber(group.vendor?.delivery_fee) === null ? null : charges.deliveryFee,
        // Same tri-state as deliveryFee: null = no surcharge on record, which
        // is not the same claim as "$0 fuel surcharge".
        fuelSurcharge: toFiniteNumber(group.vendor?.fuel_surcharge_amount) === null ? null : charges.fuelSurcharge,
        belowMinimumFee: charges.belowMinimumFee,
        feasible: charges.feasible,
        landedTotal: charges.landedTotal,
        isPickup: (group.vendor?.fulfillment_type || 'delivery') === 'pickup',
        // Whether anyone ever CHOSE that, as opposed to inheriting the column
        // default. Surfaces that offer to place the order need to tell the two
        // apart — see services/vendorFulfillmentService.
        fulfillmentAnswered: ANSWERED_SOURCES.includes(group.vendor?.fulfillment_type_source),
      };
    })
    .sort((a, b) => b.subtotal - a.subtotal);
}

/**
 * Headline numbers for the optimization screen.
 *
 * Every figure covers the COMPLETE plan. `requiredSubtotal` and
 * `choicesSubtotal` partition the same total rather than describing separate
 * orders, and they sum to `estimatedTotal` minus anything unbuyable.
 *
 * Savings come in two clearly named figures, because they answer different
 * questions and conflating them is how "$3 saved" hides a $20 delivery fee:
 *
 *   productSavings   current product subtotal − cheapest product subtotal,
 *                    fees ignored. The per-line ceiling, always "up to".
 *   landedSavings    supplied by the caller who knows both landed totals
 *                    (buildPlanEconomics) — the number recommendations use.
 *
 * The old name potentialSavings remains as an alias of productSavings for
 * one release so existing readers keep working.
 */
export function summarizePlan(planLines = []) {
  let estimatedTotal = 0;
  let requiredSubtotal = 0;
  let choicesSubtotal = 0;
  let cheapestPossibleTotal = 0;
  let unresolvedCount = 0;
  let unbuyableCount = 0;
  let packageRepairCount = 0;
  // `estimatedTotal` has always been a figure of speech — the plan's total,
  // exactly computed. Part of it can now be a genuine estimate, so the
  // estimated portion is measured and reported rather than absorbed.
  let estimatedPortion = 0;
  let estimatedLineCount = 0;
  // Coverage and target facts: a product is COVERED only when its delivered
  // quantity actually reaches the need — assignment alone is not coverage.
  let needsMetCount = 0;
  let coverageKnownCount = 0;
  let aboveTargetCount = 0;
  // Genuine price savings still available: for each line whose chosen option
  // has a trustworthy comparable price, what switching to the lowest unit
  // price would save ON THE CHOSEN QUANTITY — (selected − best) × quantity in
  // the shared comparison unit. Never fed by spend differences between
  // different quantities, and never by an unverified conversion.
  let grossPriceSavingsAvailable = 0;
  let grossPriceSavingsLineCount = 0;

  planLines.forEach((line) => {
    const cost = line.orderCost || 0;
    estimatedTotal += cost;
    if (line.costIsEstimated) {
      estimatedPortion += cost;
      estimatedLineCount += 1;
    }
    if (line.isRequired) requiredSubtotal += cost;
    if (line.hasChoice) choicesSubtotal += cost;
    cheapestPossibleTotal += line.lowestPossibleCost ?? cost;
    if (line.needsComparisonReview) unresolvedCount += 1;
    if (line.isUnbuyable) unbuyableCount += 1;
    if (line.needsPackageRepair) packageRepairCount += 1;

    const received = toFiniteNumber(line.selected?.quantityReceived);
    const need = toFiniteNumber(line.quantityNeeded);
    if (received !== null && need !== null && line.selectedOptionId
        && !line.needsPackageRepair && !line.isUnbuyable) {
      coverageKnownCount += 1;
      if (received + 1e-6 >= need) needsMetCount += 1;
      const overage = toFiniteNumber(line.selected?.overage);
      if (overage !== null && overage > 1e-6) aboveTargetCount += 1;
    }

    const selected = line.selected;
    const selectedUnit = toPositiveNumber(selected?.comparableUnitCost);
    const selectedPerPackage = toPositiveNumber(selected?.comparisonQuantityPerPackage);
    const selectedPackages = toPositiveNumber(selected?.packages);
    if (selected?.comparisonTrustworthy === true
        && selectedUnit !== null && selectedPerPackage !== null && selectedPackages !== null) {
      // Only a FULFILLABLE option can promise savings: a row whose need
      // conversion failed (orderCost/packages null) has a unit price but no
      // way to actually be bought for this line, so it may not set the bar.
      const bestUnit = (line.options || [])
        .filter((row) => !row.isSubstitute && row.comparisonTrustworthy === true
          && row.fulfillmentResolvable !== false
          && toFiniteNumber(row.orderCost) !== null
          && toPositiveNumber(row.packages) !== null)
        .map((row) => toPositiveNumber(row.comparableUnitCost))
        .filter((value) => value !== null)
        .reduce((min, value) => (min === null || value < min ? value : min), null);
      if (bestUnit !== null && bestUnit < selectedUnit - 1e-9) {
        grossPriceSavingsAvailable += (selectedUnit - bestUnit) * (selectedPackages * selectedPerPackage);
        grossPriceSavingsLineCount += 1;
      }
    }
  });

  const vendorIds = new Set(
    planLines.filter((l) => l.selectedOptionId).map(vendorGroupKey),
  );

  return {
    itemCount: planLines.length,
    estimatedTotal: roundMoney(estimatedTotal),
    requiredSubtotal: roundMoney(requiredSubtotal),
    choicesSubtotal: roundMoney(choicesSubtotal),
    productSavings: Math.max(0, roundMoney(estimatedTotal - cheapestPossibleTotal)),
    // Deprecated alias: fee-blind. Recommendations must use landed figures.
    potentialSavings: Math.max(0, roundMoney(estimatedTotal - cheapestPossibleTotal)),
    vendorOrderCount: vendorIds.size,
    optimizableCount: planLines.filter((l) => l.hasChoice).length,
    requiredCount: planLines.filter((l) => l.isRequired).length,
    unresolvedCount,
    unbuyableCount,
    packageRepairCount,
    // Coverage facts for the summary line ("6 of 6 needs met · 4 above PAR").
    needsMetCount,
    coverageKnownCount,
    aboveTargetCount,
    // Genuine price savings still available at the chosen quantities — the
    // only figure that may wear green "savings" styling. The spend-reduction
    // ceiling above (productSavings) compares DIFFERENT quantities and is a
    // purchase-total fact, not a price fact.
    grossPriceSavingsAvailable: roundMoney(grossPriceSavingsAvailable),
    grossPriceSavingsLineCount,
    // How much of estimatedTotal the vendor settles on a scale. Zero for every
    // plan with no catch-weight line, which is almost all of them — so a
    // caller can show "about" only when it is actually true.
    estimatedCostPortion: roundMoney(estimatedPortion),
    estimatedCostLineCount: estimatedLineCount,
    containsEstimatedCosts: estimatedLineCount > 0,
  };
}

/**
 * The same plan under each mode, so the tradeoff is visible instead of
 * asserted. "Lowest order cost, $1,655.25, 5 vendor orders" next to
 * "Recommended, $1,668.40, 3 vendor orders" lets a buyer decide whether $13
 * is worth two extra deliveries — a judgement no optimizer should make alone.
 */
export function comparePlanModes(args = {}) {
  const modes = [
    PLAN_MODES.RECOMMENDED,
    PLAN_MODES.LOWEST_ORDER_COST,
  ];
  if (args.preferredVendorId) modes.push(PLAN_MODES.CONSOLIDATE);

  return modes.map((mode) => {
    const plan = buildPurchasePlan({ ...args, mode });
    return {
      mode,
      label: mode === PLAN_MODES.CONSOLIDATE && args.vendorsById?.[args.preferredVendorId]
        ? `Prefer ${args.vendorsById[args.preferredVendorId].display_name}`
        : PLAN_MODE_LABELS[mode],
      estimatedTotal: plan.summary.estimatedTotal,
      vendorOrderCount: plan.summary.vendorOrderCount,
    };
  });
}

/**
 * Vendor orders small enough to be worth questioning, with what folding them
 * into another vendor would cost.
 *
 * Returned as advice, never applied. "Moving Brioche Buns to US Foods removes
 * the Local Artisan Bakery order and adds $3.50" is a sentence a buyer can
 * agree or disagree with; silently rerouting their order is not.
 */
export function suggestConsolidations(plan, vendorsById = {}) {
  const suggestions = [];
  const orders = plan?.vendorOrders || [];
  if (orders.length < 2) return suggestions;

  const smallest = [...orders]
    .filter((o) => o.vendorId !== null && !o.isPickup)
    .sort((a, b) => a.subtotal - b.subtotal);

  smallest.slice(0, 2).forEach((order) => {
    // Only worth suggesting when every line in it can be bought elsewhere.
    const movable = order.lines.filter((line) =>
      line.options.some((opt) => String(opt.option.vendor_id ?? '') !== String(order.vendorId ?? '')),
    );
    if (movable.length !== order.lines.length || !movable.length) return;

    let addedCost = 0;
    const targets = new Set();
    movable.forEach((line) => {
      const alternatives = line.options.filter(
        (opt) => String(opt.option.vendor_id ?? '') !== String(order.vendorId ?? '') && opt.orderCost !== null,
      );
      if (!alternatives.length) return;
      const best = alternatives.reduce((a, b) => (b.orderCost < a.orderCost ? b : a));
      addedCost += (best.orderCost - (line.orderCost || 0));
      if (best.option.vendor_id) targets.add(String(best.option.vendor_id));
    });

    const targetName = targets.size === 1
      ? (vendorsById[[...targets][0]]?.display_name || 'another vendor')
      : 'other vendors';

    suggestions.push({
      vendorId: order.vendorId,
      vendorName: order.vendorName,
      itemCount: order.itemCount,
      targetName,
      costDelta: roundMoney(addedCost),
      vendorOrdersAfter: orders.length - 1,
      lineIds: movable.map((l) => l.lineId),
    });
  });

  return suggestions;
}

/**
 * Builds the per-item stored comparison map the planner needs, from the same
 * reader every other surface uses. Kept here so callers hand in raw rows and
 * never assemble comparisons themselves.
 */
export function buildComparisonMap(
  itemsById = {},
  optionsByItemId = {},
  groups = null,
  {
    shoppingCandidateGraph = null,
    groupComparisonByKey = {},
    shoppingComparisonByGroupKey = {},
    canonicalItemIdByGroupId = {},
  } = {},
) {
  // With groups (buildWorkingSetGroups over confirmed EQUIVALENT pairs) every
  // grouped item's comparison is assembled over its whole group's pooled
  // options — the pool the savings review actually shops. Members whose
  // snapshots disagree with the unit drop to unresolved exactly as before;
  // the group AI pass stamps members uniformly so agreement is the steady
  // state, and this stays honest in the meantime.
  const equivalentGroups = shoppingCandidateGraph?.equivalentGroups || groups;
  const pools = equivalentGroups
    ? expandOptionsAcrossGroup(optionsByItemId, equivalentGroups)
    : (optionsByItemId || {});
  const map = {};
  const itemIds = new Set([...Object.keys(pools), ...Object.keys(itemsById || {})]);
  itemIds.forEach((itemId) => {
    const shopping = shoppingCandidateGraph
      ? getShoppingCandidatesForItem(itemId, {
        graph: shoppingCandidateGraph,
        itemsById,
        optionsByItemId,
        canonicalItemIdByGroupId,
      })
      : null;
    const groupId = equivalentGroupIdFor(equivalentGroups, itemId);
    const groupRecord = groupComparisonByKey?.[String(groupId)] || null;
    const liveShopping = shoppingComparisonByGroupKey?.[String(groupId)] || null;
    const canonicalId = liveShopping?.canonicalItemId
      ?? groupRecord?.canonical_item_id
      ?? shopping?.primaryGroup?.canonicalItemId
      ?? itemId;
    const item = itemsById[String(canonicalId)] || itemsById[itemId] || { id: itemId };
    const candidateOptions = shopping?.purchaseOptions || pools[itemId] || [];
    // The server-owned group row is the denominator authority. In particular,
    // a stale member column can still say `lb` after the Equivalent pass chose
    // `gal`; reading the member first is how Orange Juice reached the UI with
    // mixed dimensions. A live Shopping-candidate result may supersede the
    // Equivalent-only row for this review, but it is deliberately not written
    // back over either product closure.
    const comparisonUnit = liveShopping?.comparisonUnit
      ?? liveShopping?.comparison_unit
      ?? groupRecord?.comparison_unit
      ?? null;
    // A pooled group's options belong to SEVERAL items, and a package basis is
    // a fact about the option's own item. Validating a sibling's snapshot
    // against this item's unit recomputes a different fingerprint and discards
    // a perfectly good comparison — the savings review then shopped a group
    // with half its vendors silently unpriced. itemsById is already the whole
    // catalog, so each option is judged against the item it belongs to.
    const assembled = assembleStoredComparison(item, candidateOptions, {
      itemsById,
      comparisonUnit,
    });
    map[itemId] = {
      ...assembled,
      ...(liveShopping?.comparison || {}),
      comparisonUnit: liveShopping?.comparison?.comparisonUnit
        ?? liveShopping?.comparisonUnit
        ?? assembled.comparisonUnit,
      comparisonUnitLabel: liveShopping?.comparison?.comparisonUnitLabel
        ?? liveShopping?.comparisonUnit
        ?? assembled.comparisonUnitLabel,
      optionRoleById: shopping?.optionRoleById || {},
      primaryGroupId: String(groupId),
    };
  });
  return map;
}

/* ── Landed economics and candidate plans ─────────────────────────────────── */

/**
 * The plan with its fees attached: product subtotal, delivery and
 * below-minimum charges, landed total, and feasibility. A hard-minimum
 * vendor order below its minimum makes the whole plan infeasible rather than
 * quietly more expensive — it cannot be recommended, only accepted
 * explicitly.
 */
export function buildPlanEconomics(plan) {
  const landed = landedPlanTotals(plan?.vendorOrders || []);
  return {
    ...plan,
    vendorOrders: landed.orders,
    landed: {
      productSubtotal: landed.productSubtotal,
      fees: landed.fees,
      landedTotal: landed.landedTotal,
      feasible: landed.feasible,
      infeasibleCount: landed.infeasibleCount,
    },
  };
}

/**
 * Compares a user scenario with the frozen recommended baseline.
 *
 * All money comes from buildPlanEconomics → landedPlanTotals; this function
 * only subtracts two authoritative answers. It also reports operational
 * deltas and the exact line moves so every UI surface can describe the same
 * scenario without rebuilding its own calculator.
 */
export function comparePurchasePlans(recommendedPlan, scenarioPlan) {
  const recommended = buildPlanEconomics(recommendedPlan || { lines: [], vendorOrders: [], summary: {} });
  const scenario = buildPlanEconomics(scenarioPlan || { lines: [], vendorOrders: [], summary: {} });
  const scenarioByLineId = new Map(
    (scenario.lines || []).map((line) => [String(line.lineId), line]),
  );
  const changedLines = [];

  const coverageFor = (line) => {
    if (!line?.selectedOptionId || line?.needsPackageRepair || line?.isUnbuyable) return false;
    const need = toFiniteNumber(line.quantityNeeded);
    const received = toFiniteNumber(line.selected?.quantityReceived);
    if (need === null || received === null) return null;
    return received + 1e-6 >= need;
  };
  const economicLineComplete = (line) => (
    !!line?.selectedOptionId
    && toFiniteNumber(line?.orderCost) !== null
    && coverageFor(line) === true
  );
  const unresolvedReasonFor = (line) => {
    if (!line) return 'no_approved_replacement';
    if (line.isIntentionallyExcluded === true) return 'intentionally_excluded';
    if (line.needsPackageRepair) return 'package_quantity_unavailable';
    if (!line.selectedOptionId || line.isUnbuyable) return 'no_approved_replacement';
    if (toFiniteNumber(line.orderCost) === null) return 'price_unavailable';
    const coverage = coverageFor(line);
    if (coverage === false) return 'below_par';
    if (coverage === null) return 'fulfillment_quantity_unavailable';
    return null;
  };
  const vendorNameFor = (plan, line) => {
    if (!line) return null;
    const order = (plan.vendorOrders || []).find((candidate) => (
      (candidate.lines || []).some((entry) => String(entry.lineId) === String(line.lineId))
    ));
    return order?.vendorName
      || line.selected?.option?.vendor_name_text
      || null;
  };
  const objectiveQuantityReceived = (line) => {
    const packages = toFiniteNumber(line?.selected?.packages);
    const perPackage = toFiniteNumber(line?.selected?.comparisonQuantityPerPackage);
    if (packages === null || perPackage === null) return null;
    return round6(packages * perPackage);
  };
  const nullableDelta = (from, to, round = round6) => (
    from === null || to === null ? null : round(to - from)
  );

  (recommended.lines || []).forEach((from) => {
    const to = scenarioByLineId.get(String(from.lineId)) || null;
    const fromOptionId = from?.selectedOptionId == null ? null : String(from.selectedOptionId);
    const toOptionId = to?.selectedOptionId == null ? null : String(to.selectedOptionId);
    if (fromOptionId === toOptionId) return;
    const fromOrderCost = toFiniteNumber(from?.orderCost);
    const toOrderCost = toFiniteNumber(to?.orderCost);
    const fromComparableUnitCost = toFiniteNumber(from?.selected?.comparableUnitCost);
    const toComparableUnitCost = toFiniteNumber(to?.selected?.comparableUnitCost);
    const unitPriceComparisonTrustworthy = from?.selected?.comparisonTrustworthy === true
      && to?.selected?.comparisonTrustworthy === true;
    const fromObjectiveQuantityPerPackage = toFiniteNumber(
      from?.selected?.comparisonQuantityPerPackage,
    );
    const toObjectiveQuantityPerPackage = toFiniteNumber(
      to?.selected?.comparisonQuantityPerPackage,
    );
    const sameComparisonUnit = from?.comparisonUnit && to?.comparisonUnit
      && String(from.comparisonUnit) === String(to.comparisonUnit);
    const comparisonUnit = sameComparisonUnit ? from.comparisonUnit : null;
    const fromObjectiveQuantityReceived = comparisonUnit
      ? objectiveQuantityReceived(from) : null;
    const toObjectiveQuantityReceived = comparisonUnit
      ? objectiveQuantityReceived(to) : null;
    const fromQuantityReceived = toFiniteNumber(from?.selected?.quantityReceived);
    const toQuantityReceived = toFiniteNumber(to?.selected?.quantityReceived);
    const endingVsPar = toFiniteNumber(to?.selected?.overage);
    const economicImpactComplete = economicLineComplete(from) && economicLineComplete(to);
    const unresolvedReason = economicImpactComplete ? null : unresolvedReasonFor(to);
    // ── Gross price impact: the SAME quantity priced at both unit prices ──
    //
    // (previous unit price − scenario unit price) × the scenario's delivered
    // comparable quantity. Positive = genuine price savings; negative = a
    // price premium. It exists ONLY when both options share a verified
    // comparison unit — a spend difference between unlike or unconverted
    // packages is a purchase-total change, never a price claim.
    const grossPriceComparable = comparisonUnit != null
      && unitPriceComparisonTrustworthy
      && fromComparableUnitCost !== null
      && toComparableUnitCost !== null
      && toObjectiveQuantityReceived !== null;
    const grossPriceImpact = grossPriceComparable
      ? roundMoney((fromComparableUnitCost - toComparableUnitCost) * toObjectiveQuantityReceived)
      : null;
    // The counterfactual the buyer actually asks: "what would this same
    // quantity have cost at the previous vendor's unit price?"
    const equivalentQuantityCostAtPreviousUnitPrice = grossPriceComparable
      ? roundMoney(fromComparableUnitCost * toObjectiveQuantityReceived)
      : null;
    changedLines.push({
      lineId: from.lineId,
      inventoryItemId: from.inventoryItemId,
      description: from.description || to?.description || null,
      fromOptionId,
      toOptionId,
      fromVendorId: from?.vendorId == null ? null : String(from.vendorId),
      toVendorId: to?.vendorId == null ? null : String(to.vendorId),
      fromVendorName: vendorNameFor(recommended, from),
      toVendorName: vendorNameFor(scenario, to),
      itemCostDelta: economicImpactComplete
        ? nullableDelta(fromOrderCost, toOrderCost, roundMoney)
        : null,
      // Alias with the domain language used by scenario summaries/exports.
      spendDelta: economicImpactComplete
        ? nullableDelta(fromOrderCost, toOrderCost, roundMoney)
        : null,
      fromOrderCost,
      toOrderCost,
      comparisonUnit,
      needQuantity: toFiniteNumber(to?.quantityNeeded ?? from?.quantityNeeded),
      needUnit: to?.countUnit ?? from?.countUnit ?? null,
      fromComparableUnitCost: comparisonUnit ? fromComparableUnitCost : null,
      toComparableUnitCost: comparisonUnit ? toComparableUnitCost : null,
      comparableUnitCostDelta: comparisonUnit && unitPriceComparisonTrustworthy
        ? nullableDelta(fromComparableUnitCost, toComparableUnitCost)
        : null,
      unitPriceComparisonTrustworthy,
      comparisonEstimated: from?.selected?.comparisonEstimated === true
        || to?.selected?.comparisonEstimated === true,
      fromObjectiveQuantityReceived,
      toObjectiveQuantityReceived,
      fromObjectiveQuantityPerPackage: comparisonUnit
        ? fromObjectiveQuantityPerPackage : null,
      toObjectiveQuantityPerPackage: comparisonUnit
        ? toObjectiveQuantityPerPackage : null,
      fromPurchaseUnit: from?.selected?.option?.purchase_unit ?? null,
      toPurchaseUnit: to?.selected?.option?.purchase_unit ?? null,
      objectiveQuantityDelta: nullableDelta(
        fromObjectiveQuantityReceived,
        toObjectiveQuantityReceived,
      ),
      countUnit: to?.countUnit ?? from?.countUnit ?? null,
      fromQuantityReceived,
      toQuantityReceived,
      quantityReceivedDelta: nullableDelta(fromQuantityReceived, toQuantityReceived),
      endingVsPar,
      reachesPar: coverageFor(to),
      grossPriceImpact,
      equivalentQuantityCostAtPreviousUnitPrice,
      economicImpactComplete,
      unresolved: !economicImpactComplete,
      unassigned: !to?.selectedOptionId,
      reason: unresolvedReason,
    });
  });

  const pickupStops = (plan) => (plan.vendorOrders || [])
    .filter((order) => (order.itemCount || 0) > 0 && order.isPickup).length;
  const recommendedPickupStops = pickupStops(recommended);
  const scenarioPickupStops = pickupStops(scenario);
  const recommendedIncompleteCount = (recommended.lines || [])
    .filter((line) => !economicLineComplete(line)).length;
  const scenarioIncompleteCount = (scenario.lines || [])
    .filter((line) => !economicLineComplete(line)).length;
  const comparisonComplete = recommendedIncompleteCount === 0 && scenarioIncompleteCount === 0;
  const coveredProductSpendDelta = roundMoney(changedLines.reduce(
    (sum, line) => sum + (line.itemCostDelta ?? 0),
    0,
  ));
  const unresolvedItems = (recommended.lines || []).flatMap((from) => {
    const to = scenarioByLineId.get(String(from.lineId)) || null;
    if (economicLineComplete(to)) return [];
    const previousOrderCost = toFiniteNumber(from?.orderCost);
    const scenarioOrderCost = toFiniteNumber(to?.orderCost);
    return [{
      lineId: from.lineId,
      inventoryItemId: from.inventoryItemId,
      product: from.description || to?.description || 'Item',
      previousVendor: vendorNameFor(recommended, from),
      previousVendorId: from?.vendorId == null ? null : String(from.vendorId),
      previousOrderCost,
      scenarioVendor: vendorNameFor(scenario, to),
      scenarioVendorId: to?.vendorId == null ? null : String(to.vendorId),
      scenarioOrderCost,
      spendDelta: null,
      needQuantity: toFiniteNumber(to?.quantityNeeded ?? from?.quantityNeeded),
      needUnit: to?.countUnit ?? from?.countUnit ?? null,
      reachesPar: coverageFor(to),
      reason: unresolvedReasonFor(to),
    }];
  });
  const missingBaselineSpend = roundMoney(unresolvedItems.reduce((sum, item) => (
    item.previousOrderCost === null ? sum : sum + item.previousOrderCost
  ), 0));
  const knownFeeDelta = roundMoney(scenario.landed.fees - recommended.landed.fees);
  const coveredProductCount = (scenario.lines || []).filter(economicLineComplete).length;

  // Quantity difference and gross price impact across the changed lines.
  // "Purchase total change" (a cash fact) and "gross price impact" (a price
  // fact at equivalent quantities) are deliberately separate answers; a line
  // whose quantities cannot be compared makes the aggregate incomplete
  // rather than silently counting as zero.
  const quantityDeltaFor = (line) => line.objectiveQuantityDelta ?? line.quantityReceivedDelta;
  const quantityChangedProductCount = changedLines.filter((line) => {
    const delta = quantityDeltaFor(line);
    return delta !== null && Math.abs(delta) > 1e-6;
  }).length;
  const quantityComparisonUnknownCount = changedLines
    .filter((line) => quantityDeltaFor(line) === null).length;
  const grossPriceKnownLines = changedLines.filter((line) => line.grossPriceImpact !== null);
  const grossPriceImpactTotal = grossPriceKnownLines.length
    ? roundMoney(grossPriceKnownLines.reduce((sum, line) => sum + line.grossPriceImpact, 0))
    : null;
  const grossPriceImpactComplete = changedLines.length > 0
    && grossPriceKnownLines.length === changedLines.length;
  // One shared comparison unit lets the aggregate say "receives 18 bottles
  // less"; mixed units fall back to the per-product count.
  const changedComparisonUnits = [...new Set(
    changedLines.map((line) => line.comparisonUnit).filter(Boolean),
  )];
  const sharedQuantityUnit = changedLines.length > 0
    && changedComparisonUnits.length === 1
    && changedLines.every((line) => line.objectiveQuantityDelta !== null)
    ? changedComparisonUnits[0]
    : null;
  const quantityDeltaInSharedUnit = sharedQuantityUnit
    ? round6(changedLines.reduce((sum, line) => sum + line.objectiveQuantityDelta, 0))
    : null;

  return {
    recommended,
    scenario,
    changedLines,
    changedLineCount: changedLines.length,
    productSubtotalDelta: comparisonComplete
      ? roundMoney(scenario.landed.productSubtotal - recommended.landed.productSubtotal)
      : null,
    // These names make partial comparability explicit. Keep the former names
    // as aliases for one release because the worksheet already consumes them.
    coveredProductSpendDelta,
    knownProductSubtotalDelta: coveredProductSpendDelta,
    missingBaselineSpend,
    missingRecommendedSpend: missingBaselineSpend,
    unresolvedItems,
    coverageStatus: comparisonComplete ? 'complete' : 'incomplete',
    requiredProductCount: (recommended.lines || []).length,
    coveredProductCount,
    comparableScenarioTotal: comparisonComplete ? scenario.landed.landedTotal : null,
    partialAssignedSubtotal: scenario.landed.productSubtotal,
    partialLandedTotal: scenario.landed.landedTotal,
    landedTotalStatus: comparisonComplete ? 'complete' : 'incomplete',
    overallSavings: comparisonComplete
      ? roundMoney(recommended.landed.landedTotal - scenario.landed.landedTotal)
      : null,
    quantityChangedProductCount,
    quantityComparisonUnknownCount,
    grossPriceImpactTotal,
    grossPriceImpactComplete,
    sharedQuantityUnit,
    quantityDeltaInSharedUnit,
    // Fees visible in an incomplete arrangement are reconciliation facts, not
    // a comparable whole-order delta.
    knownFeeDelta,
    feeDelta: comparisonComplete ? knownFeeDelta : null,
    landedDelta: comparisonComplete
      ? roundMoney(scenario.landed.landedTotal - recommended.landed.landedTotal)
      : null,
    vendorOrderDelta: (scenario.summary?.vendorOrderCount || 0)
      - (recommended.summary?.vendorOrderCount || 0),
    pickupStopDelta: scenarioPickupStops - recommendedPickupStops,
    recommendedPickupStops,
    scenarioPickupStops,
    unresolvedLineCount: changedLines.filter((line) => line.unresolved).length,
    incompleteEconomicImpactCount: scenarioIncompleteCount,
    recommendedIncompleteEconomicImpactCount: recommendedIncompleteCount,
    economicImpactComplete: comparisonComplete,
  };
}

/**
 * Deterministic candidate plans, each with landed totals. No AI computes any
 * number here — a model may later pick among these by id and explain the
 * pick, but every figure comes from this arithmetic.
 *
 * Candidates:
 *   current            the buyer's selections as they stand
 *   lowest_product     cheapest order cost line by line, fees ignored
 *   lowest_landed      the feasible candidate with the lowest landed total
 *                      (chosen among these, marked with isLowestLanded)
 *   consolidate:<id>   one per vendor worth consolidating into
 */
export function generateCandidatePlans(args = {}) {
  const { vendorsById = {} } = args;
  const candidates = [];

  const current = buildPlanEconomics(buildPurchasePlan({ ...args, mode: PLAN_MODES.ITEM_BY_ITEM }));
  candidates.push({ planId: 'current', label: 'Current selections', plan: current });

  const lowestProduct = buildPlanEconomics(buildPurchasePlan({
    ...args, mode: PLAN_MODES.LOWEST_ORDER_COST, explicitByLineId: {},
  }));
  candidates.push({ planId: 'lowest_product', label: 'Lowest product cost', plan: lowestProduct });

  // One consolidation candidate per vendor that carries at least two of the
  // plan's items — fewer than that and "consolidate into them" is noise.
  const carryCounts = new Map();
  current.lines.forEach((line) => {
    line.options.forEach((opt) => {
      const vid = opt.option?.vendor_id;
      if (vid == null) return;
      carryCounts.set(String(vid), (carryCounts.get(String(vid)) || 0) + 1);
    });
  });
  [...carryCounts.entries()]
    .filter(([, count]) => count >= 2)
    .forEach(([vendorId]) => {
      const plan = buildPlanEconomics(buildPurchasePlan({
        ...args, mode: PLAN_MODES.CONSOLIDATE, preferredVendorId: vendorId, explicitByLineId: {},
      }));
      candidates.push({
        planId: `consolidate:${vendorId}`,
        label: `Prefer ${vendorsById[vendorId]?.display_name || 'vendor'}`,
        plan,
      });
    });

  // Award "lowest feasible landed" across the set.
  const feasible = candidates.filter((c) => c.plan.landed.feasible);
  const lowestLanded = feasible.length
    ? feasible.reduce((a, b) => (b.plan.landed.landedTotal < a.plan.landed.landedTotal ? b : a))
    : null;

  return candidates.map((c) => ({
    planId: c.planId,
    label: c.label,
    productSubtotal: c.plan.landed.productSubtotal,
    fees: c.plan.landed.fees,
    landedTotal: c.plan.landed.landedTotal,
    vendorOrderCount: c.plan.summary.vendorOrderCount,
    feasible: c.plan.landed.feasible,
    infeasibleCount: c.plan.landed.infeasibleCount,
    unresolvedCount: c.plan.summary.unresolvedCount,
    isLowestLanded: lowestLanded !== null && c.planId === lowestLanded.planId,
    plan: c.plan,
  }));
}

/* ── Vendor-choice explanation (the card) ─────────────────────────────────── */

/** Deterministic sentence for why the plan points where it points. */
export const SELECTION_REASON_TEXT = Object.freeze({
  explicit: 'You selected this vendor manually.',
  lowest_order_cost: 'Selected because it has the lowest cost to satisfy this order.',
  consolidated: 'Selected to consolidate this order with your preferred vendor.',
  preferred_fallback: 'Selected because this is your preferred vendor.',
  only: 'Selected because it is the only eligible vendor for this item.',
  unresolved: 'Selected as a fallback because the package comparison needs review.',
  no_eligible_option: 'No eligible vendor option.',
  substitute_requires_choice: 'A substitute is available, but you must approve the product change.',
  // The stored comparison's automatic picks (selectAutomaticOption). The
  // ai_estimate variant is the transparency the domain model requires: when
  // an AI-inferred pack size decided the winner, the card says so instead
  // of presenting the win as measured fact.
  best_price: 'Selected for the lowest comparable price.',
  tied_best_price: 'Selected among options tied for the lowest comparable price.',
  best_price_ai_estimate: 'Selected for the lowest comparable price — pack size AI-estimated.',
});

/**
 * Why the BUY quantity is what it is — one sentence per decisive constraint
 * (ORDER_DRIVERS). `need` is deliberately null: covering the need requires no
 * explanation, and printing one would bury the sentences that matter.
 */
export const BUY_REASON_TEXT = Object.freeze({
  need: null,
  item_minimum: "Driven by this item's minimum replenishment",
  item_increment: "Driven by this item's replenishment step",
  package_rounding: 'Rounded up to whole packages',
  vendor_moq: "Driven by the vendor's minimum order",
  vendor_increment: "Driven by the vendor's order step",
});

/**
 * The whole-plan consequence of moving one line to another option — the SAME
 * math a drop performs, extracted so the back of the card, the hover preview
 * and the drag itself can never disagree. Pure; no AI anywhere near it.
 */
export function previewLineMove(args = {}, lineId, optionId) {
  const current = buildPurchasePlan(args);
  const moved = buildPurchasePlan({
    ...args,
    explicitByLineId: { ...(args.explicitByLineId || {}), [lineId]: optionId },
  });
  const comparison = comparePurchasePlans(current, moved);
  const changed = comparison.changedLines.find((line) => String(line.lineId) === String(lineId));
  const currentLine = current.lines.find((l) => String(l.lineId) === String(lineId));
  const movedLine = moved.lines.find((l) => String(l.lineId) === String(lineId));
  const currentOverage = toFiniteNumber(currentLine?.selected?.overage);
  const movedOverage = toFiniteNumber(movedLine?.selected?.overage);
  return {
    itemCostDelta: changed
      ? changed.itemCostDelta
      : (toFiniteNumber(movedLine?.orderCost) !== null && toFiniteNumber(currentLine?.orderCost) !== null
        ? roundMoney(toFiniteNumber(movedLine.orderCost) - toFiniteNumber(currentLine.orderCost))
        : null),
    overageDelta: currentOverage === null || movedOverage === null
      ? null
      : round6(movedOverage - currentOverage),
    vendorOrdersBefore: current.summary.vendorOrderCount,
    vendorOrdersAfter: moved.summary.vendorOrderCount,
    feeDelta: comparison.feeDelta,
    landedDelta: comparison.landedDelta,
    economicImpactComplete: comparison.economicImpactComplete,
    pickupStopsBefore: comparison.recommendedPickupStops,
    pickupStopsAfter: comparison.scenarioPickupStops,
    pickupStopDelta: comparison.pickupStopDelta,
  };
}

/**
 * One row per eligible vendor option for ONE plan line: everything the card
 * needs to explain the vendor choice, front and back.
 *
 * The required quantity arrives RESOLVED (line.quantity_needed — PAR, count
 * and any upstream minimums already applied upstream, exactly once) and is
 * never recomputed here; the only minimum this function applies is the
 * option's own package MOQ, inside computePackages. Each row carries the
 * fulfillment result, the titles, the deterministic selection reason, and —
 * when planArgs is supplied — the whole-order consequence of switching to it.
 */
export function buildOptionFulfillmentComparison({
  line,
  options = [],
  comparison = null,
  vendorsById = {},
  storeId = null,
  planArgs = null,
  item = null,
  groupMembers = null,
  candidateItemIds = null,
  optionRoleById = {},
  itemsById = {},
  needBridgeByItemId = null,
} = {}) {
  const requiredQuantity = toFiniteNumber(line?.quantityNeeded ?? line?.quantity_needed) || 0;
  const requiredUnit = line?.countUnit ?? line?.count_unit ?? 'each';
  const readinessItem = { ...(item || {}), unit: item?.unit ?? item?.count_unit ?? requiredUnit };
  const lineItemId = line?.inventoryItemId ?? line?.inventory_item_id;
  const bridges = needBridgeByItemId
    ?? (lineItemId != null
      ? buildNeedBridgeIndex({
        itemsById: { [String(lineItemId)]: { ...readinessItem, id: readinessItem.id ?? lineItemId }, ...(itemsById || {}) },
        optionsByItemId: { [String(lineItemId)]: options },
      })
      : null);
  const eligible = eligibleOptionsFor(options, {
    storeId, inventoryItemId: lineItemId,
    item: readinessItem,
    groupMembers,
    candidateItemIds,
    itemsById,
    needBridgeByItemId: bridges,
  });
  const ranked = rankFulfillment({
    needQty: requiredQuantity,
    needUnit: requiredUnit,
    item: readinessItem,
    options: eligible,
    comparison,
    optionRoleById,
    itemsById,
    needBridgeByItemId: bridges,
  });
  const selectedId = line?.selectedOptionId ?? line?.selected_purchase_option_id ?? null;

  return ranked.map((row) => {
    const vendor = row.option.vendor_id != null ? vendorsById[row.option.vendor_id] || null : null;
    const isSelected = selectedId != null && String(row.optionId) === String(selectedId);
    const pkg = resolvePackageQuantity(row.option);
    // Display value for the "Minimum" row. An unstated MOQ shows the effective
    // floor of the order math: one whole package normally, none at all for a
    // cut-to-order option — "Minimum 1 lb" on a vendor that happily cuts 0.7
    // was a fabricated rule.
    const moq = toPositiveNumber(row.option.moq_packages)
      ?? (row.option.allows_fractional_packages === true ? 0 : 1);
    const entry = (comparison?.options || []).find((e) => String(e.optionId) === String(row.optionId));

    let move = null;
    if (planArgs && !isSelected && (line?.lineId ?? line?.id) != null) {
      move = previewLineMove(planArgs, line.lineId ?? line.id, row.optionId);
    }

    return {
      optionId: row.optionId,
      // The raw purchase_options row, for facts the projection below does not
      // carry — purchase-history provenance (last_operational_source_*) in
      // particular. Read-only passthrough; never mutate it.
      option: row.option,
      vendorId: row.option.vendor_id ?? null,
      vendorName: vendor?.display_name || row.option.vendor_name_text || 'Vendor',
      vendorProductDescription: row.option.vendor_description ?? null,

      requiredQuantity,
      requiredUnit,

      purchaseUnit: row.option.purchase_unit ?? 'package',
      packageDescription: row.option.pack_description ?? null,
      packagePrice: row.packagePrice,
      optionMoqPackages: moq,
      // The one decisive constraint, from the real order math — the old
      // inline ceil-vs-moq guess ignored the order increment and missed the
      // package-rounding case entirely.
      orderDriver: row.driver,
      buyReasonText: BUY_REASON_TEXT[row.driver] ?? null,
      purchaseTarget: row.purchaseTarget ?? null,
      targetDriver: row.targetDriver ?? null,
      // MOQ is worth words only when it, not the need, decided the quantity.
      moqDrivesOrder: row.driver === ORDER_DRIVERS.VENDOR_MOQ,
      // The legal ordering step, so the card can say "Ordered in whole
      // cases" / "Ordered in 0.25 lb steps" — three separate facts (minimum,
      // step, package contents), never one number standing in for another.
      orderIncrement: toPositiveNumber(row.option.order_increment),
      allowsFractionalOrder: row.option.allows_fractional_packages === true,

      inventoryQuantityPerPackage: row.quantityPerPackage,
      packagesRequired: row.packages,
      quantityReceived: row.quantityReceived,
      overage: row.overage,
      itemOrderTotal: row.orderCost,

      comparisonUnit: comparison?.comparisonUnit ?? null,
      comparisonQuantityPerPackage: entry?.quantityPerPurchasePackage ?? null,
      comparableUnitCost: row.comparableUnitCost,
      comparisonTrustworthy: row.comparisonTrustworthy === true,
      // What this option actually delivers, in the shared comparison unit —
      // the denominator every equivalent-quantity price claim must use.
      comparableQuantityReceived: (() => {
        const perPackage = toPositiveNumber(entry?.quantityPerPurchasePackage);
        const packages = toPositiveNumber(row.packages);
        return perPackage !== null && packages !== null ? round6(packages * perPackage) : null;
      })(),
      // How we know the pack size — the transparency the domain model
      // requires beside every comparable figure. data_quality rides on
      // group-pass snapshots; older snapshots simply have none.
      comparisonSource: entry?.source ?? null,
      comparisonConfidence: entry?.confidence ?? null,
      comparisonEstimated: entry?.estimated === true,
      comparisonBasisVerified: entry?.basisVerified !== false,
      comparisonDataQuality: entry?.dataQuality ?? entry?.data_quality ?? null,
      comparisonAssumption: entry?.assumption ?? null,

      candidateRole: row.candidateRole,
      isSubstitute: row.isSubstitute === true,
      isLowestShoppingUnitPrice: row.isLowestShoppingUnitPrice === true,
      isLowestSubstitutePrice: row.isLowestSubstitutePrice === true,
      substituteSavingsPerUnit: row.substituteSavingsPerUnit ?? null,

      isSelected,
      isLowestOrderCost: row.isLowestOrderCost,
      isBestUnitPrice: row.isBestUnitPrice,
      selectionReason: isSelected ? (line?.selectionReason ?? null) : null,
      selectionReasonText: isSelected
        ? (SELECTION_REASON_TEXT[line?.selectionReason] || null)
        : null,

      itemCostDifferenceFromSelected: null, // filled below, needs the selected row
      landedOrderDifferenceIfSelected: move?.landedDelta ?? null,
      vendorCountDifferenceIfSelected: move
        ? move.vendorOrdersAfter - move.vendorOrdersBefore : null,
      pickupStopDifferenceIfSelected: move?.pickupStopDelta ?? null,
      feeDifferenceIfSelected: move?.feeDelta ?? null,

      conversionSource: row.needConversionSource,
      needsPackageReview: pkg.requiresReview,
      needsComparisonReview: !entry || !comparison?.comparisonUnit,
      comparisonReviewReason: !entry
        ? (comparison?.unresolvedDetails || []).find(
          (detail) => String(detail.optionId) === String(row.optionId),
        )?.reason || 'quantity_confirmation_needed'
        : null,
      warnings: row.packageUnresolved ? ['unresolved_package'] : [],
    };
  }).map((row, _i, rows) => {
    const selected = rows.find((r) => r.isSelected) || null;
    // Equivalent-quantity price comparison against the current choice: both
    // sides must share the verified comparison unit, or no price claim exists.
    const grossComparable = selected && !row.isSelected
      && row.comparisonUnit != null
      && row.comparisonTrustworthy && selected.comparisonTrustworthy
      && row.comparableUnitCost != null && selected.comparableUnitCost != null
      && row.comparableQuantityReceived != null;
    return {
      ...row,
      itemCostDifferenceFromSelected: selected && row.itemOrderTotal != null && selected.itemOrderTotal != null
        ? roundMoney(row.itemOrderTotal - selected.itemOrderTotal)
        : null,
      // Delivered-quantity difference in the comparison unit (this option −
      // the current choice), so a cheaper checkout that buys less product can
      // say so instead of wearing a savings label.
      comparableQuantityDifferenceFromSelected: selected
        && row.comparableQuantityReceived != null
        && selected.comparableQuantityReceived != null
        ? round6(row.comparableQuantityReceived - selected.comparableQuantityReceived)
        : null,
      // (current unit price − this option's unit price) × this option's
      // delivered quantity. Positive = gross price savings from switching;
      // negative = gross price premium. Before fees and tax.
      grossPriceDifferenceFromSelected: grossComparable
        ? roundMoney((selected.comparableUnitCost - row.comparableUnitCost) * row.comparableQuantityReceived)
        : null,
    };
  });
}
